/**
 * EmailScheduler — Schedule & Automate tab
 * - One-shot scheduled sends with calendar + clock
 * - AI "best send time" suggestion
 * - 4-week drip campaigns (weekly cadence)
 * - List view + monthly calendar view of upcoming sends
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import {
  CalendarClock,
  Sparkles,
  Plus,
  Trash2,
  Pause,
  Play,
  Loader2,
  Mail,
  CalendarDays,
  ListChecks,
  Brain,
  RotateCw,
} from "lucide-react";
import { API } from "@/App";

const DEPARTMENTS = [
  "Supply Chain",
  "Materials Management",
  "Sterile Processing",
  "Environmental Services",
  "Facilities Management",
  "Infection Prevention",
  "Quality",
  "Administration",
  "C-Suite",
  "Procurement",
];

const TIMEZONES = [
  { v: "America/New_York", l: "Eastern (ET)" },
  { v: "America/Chicago", l: "Central (CT)" },
  { v: "America/Denver", l: "Mountain (MT)" },
  { v: "America/Los_Angeles", l: "Pacific (PT)" },
];

function fmtLocalDateTime(iso, tz) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: tz || "America/New_York",
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

export default function EmailScheduler({ token, templates = [] }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [view, setView] = useState("list"); // list | calendar | drips
  const [schedules, setSchedules] = useState([]);
  const [drips, setDrips] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showCreateOneShot, setShowCreateOneShot] = useState(false);
  const [showCreateDrip, setShowCreateDrip] = useState(false);

  // ----- Fetch -----
  const fetchData = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        fetch(`${API}/email-schedules`, { headers }).then(r => r.json()),
        fetch(`${API}/email-drips`, { headers }).then(r => r.json()),
      ]);
      setSchedules(s.schedules || []);
      setDrips(d.drips || []);
    } catch { toast.error("Failed to load schedules"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const cancelSchedule = async (id) => {
    if (!window.confirm("Cancel this scheduled send?")) return;
    try {
      const r = await fetch(`${API}/email-schedules/${id}`, { method: "DELETE", headers });
      if (!r.ok) throw new Error("Cancel failed");
      toast.success("Cancelled");
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const toggleDrip = async (drip) => {
    const newStatus = drip.status === "active" ? "paused" : "active";
    try {
      const r = await fetch(`${API}/email-drips/${drip.id}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!r.ok) throw new Error("Update failed");
      toast.success(`Drip ${newStatus}`);
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  // Upcoming schedules sorted by time
  const upcoming = useMemo(
    () => schedules.filter(s => ["scheduled", "sending"].includes(s.status))
                   .sort((a, b) => (a.scheduled_for_utc || "").localeCompare(b.scheduled_for_utc || "")),
    [schedules]
  );
  const past = useMemo(
    () => schedules.filter(s => ["sent", "cancelled", "failed"].includes(s.status))
                   .sort((a, b) => (b.scheduled_for_utc || "").localeCompare(a.scheduled_for_utc || ""))
                   .slice(0, 50),
    [schedules]
  );

  return (
    <div className="space-y-4" data-testid="email-scheduler">
      {/* Header bar */}
      <Card className="bg-gradient-to-br from-violet-50 to-blue-50 border-violet-200">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-violet-900">
                <CalendarClock className="w-5 h-5" /> Schedule &amp; Automate
              </CardTitle>
              <CardDescription className="text-violet-700/80">
                Schedule one-shot sends, run AI-picked send times, or build 4-week drip campaigns.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowCreateOneShot(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="new-schedule-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Schedule a send
              </Button>
              <Button
                onClick={() => setShowCreateDrip(true)}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="new-drip-btn"
              >
                <RotateCw className="w-4 h-4 mr-1" /> New 4-week drip
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* View switcher */}
      <Tabs value={view} onValueChange={setView}>
        <TabsList>
          <TabsTrigger value="list"><ListChecks className="w-4 h-4 mr-1" /> Upcoming ({upcoming.length})</TabsTrigger>
          <TabsTrigger value="calendar"><CalendarDays className="w-4 h-4 mr-1" /> Calendar</TabsTrigger>
          <TabsTrigger value="drips"><RotateCw className="w-4 h-4 mr-1" /> Drips ({drips.length})</TabsTrigger>
        </TabsList>

        {/* ---- LIST VIEW ---- */}
        <TabsContent value="list" className="mt-4">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
          ) : (
            <Card>
              <CardHeader><CardTitle className="text-sm">Upcoming Sends</CardTitle></CardHeader>
              <CardContent>
                {upcoming.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">
                    No scheduled sends yet. Click <strong>Schedule a send</strong> above to create one.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {upcoming.map(s => (
                      <ScheduleRow key={s.id} s={s} onCancel={cancelSchedule} />
                    ))}
                  </div>
                )}

                {past.length > 0 && (
                  <>
                    <p className="text-xs uppercase font-bold text-slate-400 tracking-wider mt-6 mb-2">Recent history</p>
                    <div className="space-y-2 opacity-75">
                      {past.slice(0, 10).map(s => (
                        <ScheduleRow key={s.id} s={s} onCancel={() => {}} isHistory />
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---- CALENDAR VIEW ---- */}
        <TabsContent value="calendar" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <CalendarView schedules={schedules} drips={drips} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- DRIPS VIEW ---- */}
        <TabsContent value="drips" className="mt-4">
          {drips.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm text-slate-500">
              No drip campaigns yet. Click <strong>New 4-week drip</strong> to build one.
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {drips.map(d => <DripRow key={d.id} d={d} onToggle={toggleDrip} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ---- MODALS ---- */}
      {showCreateOneShot && (
        <CreateOneShotModal
          open={showCreateOneShot}
          onOpenChange={setShowCreateOneShot}
          templates={templates}
          token={token}
          onCreated={() => { setShowCreateOneShot(false); fetchData(); }}
        />
      )}
      {showCreateDrip && (
        <CreateDripModal
          open={showCreateDrip}
          onOpenChange={setShowCreateDrip}
          templates={templates}
          token={token}
          onCreated={() => { setShowCreateDrip(false); fetchData(); }}
        />
      )}
    </div>
  );
}

// =========================================================================
// Row components
// =========================================================================
function ScheduleRow({ s, onCancel, isHistory }) {
  const statusColors = {
    scheduled: "bg-blue-100 text-blue-700",
    sending: "bg-amber-100 text-amber-700",
    sent: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-slate-100 text-slate-600",
    failed: "bg-rose-100 text-rose-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`schedule-row-${s.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Mail className="w-4 h-4 text-slate-400" />
          <p className="font-medium text-sm text-slate-900 truncate">{s.name}</p>
          <Badge className={statusColors[s.status] || "bg-slate-100"}>{s.status}</Badge>
          {s.drip_id && <Badge variant="outline" className="text-violet-700 border-violet-300 text-[10px]"><RotateCw className="w-3 h-3 mr-0.5" />drip</Badge>}
        </div>
        <p className="text-xs text-slate-500 mt-0.5">
          <CalendarClock className="w-3 h-3 inline mr-1" />
          {fmtLocalDateTime(s.scheduled_for_utc, s.timezone)} ({s.timezone || "ET"})
        </p>
        <p className="text-xs text-slate-400 truncate">
          Template: <span className="text-slate-600">{s.template_id}</span>
          {s.recipients?.length && ` · ${s.recipients.length} recipients`}
          {s.department_filter?.length && ` · depts: ${s.department_filter.join(", ")}`}
          {s.sent_count > 0 && ` · ✓ ${s.sent_count} sent`}
          {s.failed_count > 0 && ` · ✗ ${s.failed_count} failed`}
        </p>
      </div>
      {!isHistory && s.status === "scheduled" && (
        <Button
          variant="ghost" size="sm"
          onClick={() => onCancel(s.id)}
          className="text-red-600 hover:bg-red-50"
          data-testid={`cancel-${s.id}-btn`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}

function DripRow({ d, onToggle }) {
  const statusColors = {
    active: "bg-emerald-100 text-emerald-700",
    paused: "bg-amber-100 text-amber-700",
    cancelled: "bg-slate-100 text-slate-600",
    completed: "bg-blue-100 text-blue-700",
  };
  return (
    <Card data-testid={`drip-row-${d.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <RotateCw className="w-4 h-4 text-violet-600" />
              <p className="font-semibold text-slate-900">{d.name}</p>
              <Badge className={statusColors[d.status]}>{d.status}</Badge>
              <Badge variant="outline" className="text-xs">{d.steps?.length || 0} weeks</Badge>
            </div>
            {d.description && <p className="text-sm text-slate-500 mt-1">{d.description}</p>}
            <p className="text-xs text-slate-400 mt-1">
              Starts {fmtLocalDateTime(d.start_date, d.timezone)} · {d.send_hour}:00 {d.timezone}
            </p>
            <div className="mt-3 flex gap-2 flex-wrap">
              {(d.steps || []).map((step, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  W{step.week_offset}: {step.template_name || step.template_id}
                </Badge>
              ))}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => onToggle(d)} data-testid={`toggle-drip-${d.id}`}>
            {d.status === "active" ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
            {d.status === "active" ? "Pause" : "Resume"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CalendarView({ schedules, drips }) {
  const [date, setDate] = useState(new Date());
  // Build a set of YYYY-MM-DD strings that have schedules
  const sendDays = useMemo(() => {
    const m = new Map();
    for (const s of schedules) {
      if (!s.scheduled_for_utc || s.status === "cancelled") continue;
      const dStr = new Date(s.scheduled_for_utc).toISOString().slice(0, 10);
      if (!m.has(dStr)) m.set(dStr, []);
      m.get(dStr).push(s);
    }
    return m;
  }, [schedules]);

  const selectedKey = date ? date.toISOString().slice(0, 10) : null;
  const selectedDaySends = selectedKey ? (sendDays.get(selectedKey) || []) : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          className="rounded-md border"
          modifiers={{ hasSends: (d) => sendDays.has(d.toISOString().slice(0, 10)) }}
          modifiersClassNames={{ hasSends: "bg-violet-100 font-bold text-violet-900 ring-1 ring-violet-300" }}
        />
        <p className="text-xs text-slate-500 mt-2"><span className="inline-block w-3 h-3 rounded-sm bg-violet-200 mr-1.5 align-middle"></span> Violet days have scheduled sends</p>
        <p className="text-xs text-slate-400 mt-1">{drips.filter(d => d.status === "active").length} active drips · {schedules.filter(s => s.status === "scheduled").length} pending sends total</p>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
          Sends on {date ? date.toLocaleDateString("en-US", { month:"long", day:"numeric", year:"numeric" }) : "—"}
        </p>
        {selectedDaySends.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Nothing scheduled this day.</p>
        ) : (
          <div className="space-y-2">
            {selectedDaySends.map(s => <ScheduleRow key={s.id} s={s} onCancel={() => {}} isHistory />)}
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// CREATE ONE-SHOT MODAL
// =========================================================================
function CreateOneShotModal({ open, onOpenChange, templates, token, onCreated }) {
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("10:00");
  const [tz, setTz] = useState("America/New_York");
  const [recipientMode, setRecipientMode] = useState("departments"); // departments | manual
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [manualEmails, setManualEmails] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const askAi = async () => {
    setAiBusy(true);
    try {
      const r = await fetch(`${API}/email-schedules/ai-suggest-time`, {
        method: "POST", headers,
        body: JSON.stringify({ departments: selectedDepts, target_audience: "hospitals", template_id: templateId }),
      });
      if (!r.ok) throw new Error("AI suggestion failed");
      const data = await r.json();
      const s = data.suggestion;
      // Apply to form: pick the next occurrence of best_day_of_week at best_time
      const dayMap = { Sunday:0, Monday:1, Tuesday:2, Wednesday:3, Thursday:4, Friday:5, Saturday:6 };
      const targetDow = dayMap[s.best_day_of_week] ?? 2;
      const now = new Date();
      let candidate = new Date(now); candidate.setHours(0,0,0,0);
      while (candidate.getDay() !== targetDow || candidate <= now) candidate.setDate(candidate.getDate() + 1);
      setDate(candidate);
      setTime(s.best_time || "10:00");
      setTz(s.timezone || "America/New_York");
      toast.success(`AI picked ${s.best_day_of_week} ${s.best_time} ${s.timezone}`, {
        description: s.reason, duration: 8000,
      });
    } catch (e) { toast.error(e.message); }
    finally { setAiBusy(false); }
  };

  const submit = async () => {
    if (!name || !templateId || !date) {
      toast.error("Name, template, and date are required");
      return;
    }
    setSaving(true);
    try {
      const localISO = `${date.toISOString().slice(0, 10)}T${time}:00`;
      const payload = {
        name, template_id: templateId,
        scheduled_for: localISO, timezone: tz,
      };
      if (recipientMode === "departments") {
        payload.department_filter = selectedDepts;
      } else {
        payload.recipients = manualEmails.split(/[\s,;]+/).filter(Boolean).map(e => ({ email: e.trim() }));
      }
      const r = await fetch(`${API}/email-schedules`, { method: "POST", headers, body: JSON.stringify(payload) });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Failed to schedule");
      }
      toast.success("Send scheduled");
      onCreated();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-blue-600" /> Schedule a Send</DialogTitle>
          <DialogDescription>Pick a template, recipients, and a date/time. Use AI to suggest the optimal send window.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Schedule name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Filmop EVS push — Week 1" data-testid="oneshot-name" />
          </div>

          <div>
            <Label>Template</Label>
            <select className="w-full border rounded-md px-3 py-2 text-sm" value={templateId} onChange={e => setTemplateId(e.target.value)} data-testid="oneshot-template">
              <option value="">— Select template —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <div className="border rounded-lg p-3 bg-slate-50">
            <p className="text-xs font-bold uppercase text-slate-500 mb-2">Recipients</p>
            <div className="flex gap-2 mb-2">
              {["departments", "manual"].map(m => (
                <button
                  key={m} type="button"
                  onClick={() => setRecipientMode(m)}
                  className={`px-3 py-1 text-xs rounded ${recipientMode===m ? "bg-blue-600 text-white" : "bg-white border"}`}
                  data-testid={`recipient-mode-${m}`}
                >{m === "departments" ? "By department" : "Manual emails"}</button>
              ))}
            </div>
            {recipientMode === "departments" ? (
              <div className="flex flex-wrap gap-1.5">
                {DEPARTMENTS.map(d => {
                  const on = selectedDepts.includes(d);
                  return (
                    <button key={d} type="button"
                      onClick={() => setSelectedDepts(p => on ? p.filter(x => x !== d) : [...p, d])}
                      className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700"}`}
                    >{d}</button>
                  );
                })}
              </div>
            ) : (
              <textarea
                value={manualEmails}
                onChange={e => setManualEmails(e.target.value)}
                placeholder="comma- or newline-separated emails"
                className="w-full border rounded p-2 text-sm font-mono h-24"
                data-testid="oneshot-manual-emails"
              />
            )}
          </div>

          <div className="border rounded-lg p-3">
            <div className="flex justify-between items-center mb-2">
              <p className="text-xs font-bold uppercase text-slate-500">When</p>
              <Button
                type="button" variant="outline" size="sm"
                onClick={askAi} disabled={aiBusy}
                className="text-violet-700 border-violet-300 hover:bg-violet-50"
                data-testid="ai-suggest-time-btn"
              >
                {aiBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Brain className="w-3.5 h-3.5 mr-1" />}
                AI: pick best time
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-3 sm:col-span-1">
                <Label className="text-xs">Date</Label>
                <Calendar mode="single" selected={date} onSelect={setDate} className="border rounded-md" />
              </div>
              <div>
                <Label className="text-xs">Time (24h)</Label>
                <Input type="time" value={time} onChange={e => setTime(e.target.value)} data-testid="oneshot-time" />
              </div>
              <div>
                <Label className="text-xs">Timezone</Label>
                <select className="w-full border rounded-md px-2 py-2 text-sm" value={tz} onChange={e => setTz(e.target.value)} data-testid="oneshot-tz">
                  {TIMEZONES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="oneshot-submit">
            {saving ? "Scheduling…" : "Schedule send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// CREATE DRIP MODAL  (4-week weekly cadence)
// =========================================================================
function CreateDripModal({ open, onOpenChange, templates, token, onCreated }) {
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [startDate, setStartDate] = useState(undefined);
  const [sendHour, setSendHour] = useState(10);
  const [tz, setTz] = useState("America/New_York");
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [steps, setSteps] = useState([
    { template_id: "", week_offset: 0 },
    { template_id: "", week_offset: 1 },
    { template_id: "", week_offset: 2 },
    { template_id: "", week_offset: 3 },
  ]);
  const [saving, setSaving] = useState(false);

  const setStep = (i, tplId) => {
    setSteps(prev => prev.map((s, idx) => idx === i ? { ...s, template_id: tplId, template_name: templates.find(t => t.id === tplId)?.name } : s));
  };

  const submit = async () => {
    if (!name || !startDate) { toast.error("Name and start date required"); return; }
    if (steps.some(s => !s.template_id)) { toast.error("All 4 weeks must have a template"); return; }
    if (selectedDepts.length === 0) { toast.error("Pick at least one department"); return; }
    setSaving(true);
    try {
      const payload = {
        name, description: desc,
        start_date: startDate.toISOString(),
        send_hour: Number(sendHour), timezone: tz,
        steps,
        department_filter: selectedDepts,
      };
      const r = await fetch(`${API}/email-drips`, { method: "POST", headers, body: JSON.stringify(payload) });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Drip creation failed");
      }
      toast.success("4-week drip scheduled — 4 weekly sends queued");
      onCreated();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RotateCw className="w-5 h-5 text-violet-600" /> New 4-Week Drip Campaign</DialogTitle>
          <DialogDescription>
            Send 4 different templates on a weekly cadence to the same audience.
            Recipients who reply are auto-paused so they don&apos;t get the rest of the series.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Campaign name</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Water Purification 4-Week" data-testid="drip-name" /></div>
            <div><Label>Description (optional)</Label><Input value={desc} onChange={e => setDesc(e.target.value)} /></div>
          </div>

          <div className="border rounded-lg p-3 bg-slate-50">
            <p className="text-xs font-bold uppercase text-slate-500 mb-2">Audience (departments)</p>
            <div className="flex flex-wrap gap-1.5">
              {DEPARTMENTS.map(d => {
                const on = selectedDepts.includes(d);
                return (
                  <button key={d} type="button"
                    onClick={() => setSelectedDepts(p => on ? p.filter(x => x !== d) : [...p, d])}
                    className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700"}`}
                  >{d}</button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Start date</Label>
              <Calendar mode="single" selected={startDate} onSelect={setStartDate} className="border rounded-md" />
            </div>
            <div>
              <Label>Send hour (local)</Label>
              <Input type="number" min="0" max="23" value={sendHour} onChange={e => setSendHour(e.target.value)} />
              <p className="text-xs text-slate-400 mt-1">e.g., 10 = 10am local</p>
            </div>
            <div>
              <Label>Timezone</Label>
              <select className="w-full border rounded-md px-2 py-2 text-sm" value={tz} onChange={e => setTz(e.target.value)}>
                {TIMEZONES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
          </div>

          <div className="border-2 border-violet-200 rounded-lg p-3 bg-violet-50/30">
            <p className="text-xs font-bold uppercase text-violet-700 mb-2">4 Weekly Steps</p>
            <div className="space-y-2">
              {steps.map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Badge className="bg-violet-600 text-white">Week {i}</Badge>
                  <select
                    className="flex-1 border rounded-md px-2 py-1.5 text-sm bg-white"
                    value={s.template_id}
                    onChange={e => setStep(i, e.target.value)}
                    data-testid={`drip-step-${i}-template`}
                  >
                    <option value="">— pick template —</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="drip-submit">
            {saving ? "Creating…" : "Start drip"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
